import { api } from './client';
import type { FetchParams } from '../components/shared';

// ── Types ──

export interface MRHeader {
  [key: string]: unknown;
  id: number;
  mrCode: string;
  departmentId: number;
  requestedById: number;
  requestDate: string;
  neededBy: string | null;
  urgency: string;
  status: string;
  notes: string | null;
  lineCount: number;
  mrTotal: number;
  poCount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count: { lines: number };
}

export interface MRLine {
  id: number;
  mrHeaderId: number;
  lineNo: number;
  catalogItemId: number | null;
  partNumber: string | null;
  itemDescription: string;
  categoryId: number | null;
  vendorId: number | null;
  qty: number;
  unitId: number | null;
  unitPrice: number;
  currencyId: number | null;
  lineTotal: number;
  lineApprovalStatus: string;
  lineStatus: string;
  lineNotes: string | null;
}

export interface MRDetail extends MRHeader {
  lines: MRLine[];
}

export interface POHeader {
  [key: string]: unknown;
  id: number;
  poCode: string;
  departmentId: number;
  vendorId: number;
  poDate: string;
  createdById: number;
  status: string;
  notes: string | null;
  poTotal: number;
  actualTotal: number;
  receivedValue: number;
  openValue: number;
  lineCount: number;
  receivedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  _count: { lines: number; grns: number };
}

export interface POLine {
  id: number;
  poHeaderId: number;
  lineNo: number;
  mrLineId: number | null;
  catalogItemId: number | null;
  partNumber: string | null;
  itemDescription: string;
  vendorId: number | null;
  qty: number;
  unitId: number | null;
  unitPrice: number;
  currencyId: number | null;
  lineTotal: number;
  actualUnitPrice: number | null;
  actualLineTotal: number | null;
  priceVariance: number | null;
  totalReceived: number;
  balanceQty: number;
  returnedQty: number;
}

export interface PODetail extends POHeader {
  lines: POLine[];
  grns: GRNDetail[];
}

export interface GRNHeader {
  [key: string]: unknown;
  id: number;
  grnCode: string;
  poHeaderId: number;
  deliveryNoteRef: string | null;
  supplierInvoice: string | null;
  receivedById: number;
  receivedOn: string;
  notes: string | null;
  lineCount: number;
  createdAt: string;
  updatedAt: string;
  poHeader: { poCode: string; vendorId: number; status: string };
  _count: { lines: number };
}

export interface GRNLine {
  id: number;
  grnHeaderId: number;
  poLineId: number;
  deliveredQty: number;
  acceptedQty: number;
  rejectedQty: number;
  inspectionStatus: string;
  inspectionNotes: string | null;
  rejectReason: string | null;
}

export interface GRNDetail extends GRNHeader {
  lines: GRNLine[];
}

export interface PurchReturn {
  [key: string]: unknown;
  id: number;
  returnCode: string;
  grnLineId: number;
  returnQty: number;
  returnReason: string;
  reasonCode: string | null;
  returnDate: string;
  returnedById: number;
  status: string;
  settlementType: string | null;
  creditNoteRef: string | null;
  refundRef: string | null;
  refundAmount: number | null;
  returnValue: number;
  carrierName: string | null;
  trackingNumber: string | null;
  createdAt: string;
  updatedAt: string;
  grnLine: {
    id: number;
    grnHeaderId: number;
    poLineId: number;
    catalogItemId: number | null;
    acceptedQty: number;
    inspectionStatus: string;
    grnHeader: { grnCode: string };
  };
}

// ── API Functions ──

function buildQuery(params: FetchParams): Record<string, string> {
  const q: Record<string, string> = {
    page: String(params.page),
    pageSize: String(params.pageSize),
  };
  if (params.filters) {
    for (const [key, filter] of Object.entries(params.filters)) {
      if (filter.value !== undefined && filter.value !== null) {
        q[key] = String(filter.value);
      }
    }
  }
  return q;
}

// ── Material Requests ──

export async function fetchMRs(params: FetchParams): Promise<{ data: MRHeader[]; total: number }> {
  const res = await api.get('/purchasing/mrs', { params: buildQuery(params) });
  return { data: res.data.data, total: res.data.total };
}

export async function getMR(id: number): Promise<MRDetail> {
  const res = await api.get(`/purchasing/mrs/${id}`);
  return res.data;
}

export async function createMR(data: {
  departmentId: number;
  neededBy?: string;
  urgency?: string;
  notes?: string;
  lines: Array<{
    itemDescription: string;
    qty: number;
    unitPrice: number;
    vendorId?: number;
    partNumber?: string;
    categoryId?: number;
    unitId?: number;
    currencyId?: number;
  }>;
}): Promise<MRDetail> {
  const res = await api.post('/purchasing/mrs', data);
  return res.data;
}

export async function submitMR(id: number): Promise<MRDetail> {
  const res = await api.post(`/purchasing/mrs/${id}/submit`);
  return res.data;
}

export async function approveMRLines(
  id: number,
  approvals: Array<{ lineId: number; status: 'approved' | 'rejected'; reason?: string }>,
): Promise<MRDetail> {
  const res = await api.post(`/purchasing/mrs/${id}/approve`, { approvals });
  return res.data;
}

// ── Purchase Orders ──

export async function fetchPOs(params: FetchParams): Promise<{ data: POHeader[]; total: number }> {
  const res = await api.get('/purchasing/pos', { params: buildQuery(params) });
  return { data: res.data.data, total: res.data.total };
}

export async function getPO(id: number): Promise<PODetail> {
  const res = await api.get(`/purchasing/pos/${id}`);
  return res.data;
}

export async function fetchUnorderedLines(departmentId?: number) {
  const params = departmentId ? { departmentId: String(departmentId) } : {};
  const res = await api.get('/purchasing/pos/unordered-lines', { params });
  return res.data.data as MRLine[];
}

export async function createPO(mrLineIds: number[], notes?: string): Promise<PODetail[]> {
  const res = await api.post('/purchasing/pos', { mrLineIds, notes, reviewed: true });
  return res.data.data;
}

export async function updatePOStatus(
  id: number,
  status: string,
  reason?: string,
): Promise<POHeader> {
  const res = await api.put(`/purchasing/pos/${id}/status`, { status, reason });
  return res.data;
}

// ── Goods Receipt Notes ──

export async function fetchGRNs(params: FetchParams): Promise<{ data: GRNHeader[]; total: number }> {
  const res = await api.get('/purchasing/grns', { params: buildQuery(params) });
  return { data: res.data.data, total: res.data.total };
}

export async function getGRN(id: number): Promise<GRNDetail> {
  const res = await api.get(`/purchasing/grns/${id}`);
  return res.data;
}

export async function createGRN(data: {
  poId: number;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  receivedOn?: string;
  notes?: string;
  lines: Array<{
    poLineId: number;
    deliveredQty: number;
    acceptedQty: number;
    rejectedQty?: number;
    inspectionStatus?: string;
    inspectionNotes?: string;
  }>;
}): Promise<GRNDetail> {
  const res = await api.post('/purchasing/grns', data);
  return res.data;
}

// ── Returns ──

export async function fetchReturns(params: FetchParams): Promise<{ data: PurchReturn[]; total: number }> {
  const res = await api.get('/purchasing/returns', { params: buildQuery(params) });
  return { data: res.data.data, total: res.data.total };
}

export async function getReturn(id: number): Promise<PurchReturn> {
  const res = await api.get(`/purchasing/returns/${id}`);
  return res.data;
}

export async function createReturn(data: {
  grnLineId: number;
  returnQty: number;
  returnReason: string;
  reasonCode?: string;
}): Promise<PurchReturn> {
  const res = await api.post('/purchasing/returns', data);
  return res.data;
}

export async function approveReturn(id: number): Promise<PurchReturn> {
  const res = await api.post(`/purchasing/returns/${id}/approve`);
  return res.data;
}

export async function settleReturn(
  id: number,
  data: { settlementType: 'credit' | 'refund'; creditNoteRef?: string; refundRef?: string; refundAmount?: number },
): Promise<PurchReturn> {
  const res = await api.post(`/purchasing/returns/${id}/settle`, data);
  return res.data;
}
